import {DOCUMENT, inject, Injectable, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import type {AppLocale} from 'compob2b-libs';
import {DEFAULT_LOCALE, Environment, LOCAL_STORAGE, SUPPORTED_LOCALES, isSupportedLocale} from 'compob2b-libs';
import {BehaviorSubject, Observable} from 'rxjs';

// AppLocale/DEFAULT_LOCALE/SUPPORTED_LOCALES теперь в compob2b-libs (общая модель
// локали). Реэкспортируем тип, чтобы существующие импорты `from './locale.service'`
// (свитчер, фильтр, спека) продолжали работать без правок.
export type {AppLocale} from 'compob2b-libs';

const STORAGE_KEY = 'compo.locale';

@Injectable({providedIn: 'root'})
export class LocaleService {
  private readonly localStorage = inject(LOCAL_STORAGE);
  private readonly environment = inject(Environment);
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly locale$ = new BehaviorSubject<AppLocale>(this.readInitial());

  /**
   * Локали, доступные на текущей витрине (из её `targetLocales`). `null` = ограничения
   * нет (двуязычный дефолт / кабинет). Конфигурируется на бутстрапе витрины
   * (showcaseLocaleGuard) ДО первых контент-запросов.
   */
  private allowed: AppLocale[] | null = null;

  constructor() {
    // <html lang> держим в синхроне с текущей локалью (screen readers / :lang() /
    // SEO). Свитчер делает location.reload(), так что значение переустановится на
    // каждом bootstrap; подписка также отражает программный set() до перезагрузки.
    if (isPlatformBrowser(this.platformId)) {
      this.locale$.subscribe(locale => {
        const el = this.document?.documentElement;
        if (el) { el.lang = locale; }
      });
    }
  }

  get current(): AppLocale {
    return this.locale$.value;
  }

  changes(): Observable<AppLocale> {
    return this.locale$.asObservable();
  }

  /** Локали, доступные на текущей витрине; вне витрины — все глобально поддерживаемые. */
  get availableLocales(): readonly AppLocale[] {
    return this.allowed?.length ? this.allowed : SUPPORTED_LOCALES;
  }

  /**
   * Конфигурирует локаль под витрину по её `targetLocales`:
   *  - один язык → форс (current переключается на него; localStorage НЕ трогаем, чтобы
   *    не затирать глобальный выбор пользователя — форс живёт на сессию/перезагрузку);
   *  - несколько → свитчер виден; если текущая локаль вне набора — на первый (дефолт витрины);
   *  - пусто/невалидно → ограничений нет (двуязычное поведение по умолчанию).
   * Вызывать ДО первых контент-запросов, чтобы Accept-Language ушёл уже правильным.
   */
  configureForShowcase(targetLocales: readonly string[] | null | undefined): void {
    const valid = (targetLocales ?? []).filter((l): l is AppLocale => isSupportedLocale(l));
    this.allowed = valid.length ? [...new Set(valid)] : null;
    const avail = this.availableLocales;
    if (!avail.includes(this.locale$.value)) {
      this.locale$.next(avail[0]);   // форс/клэмп; подписка обновит <html lang>
    }
  }

  set(locale: AppLocale) {
    if (!this.availableLocales.includes(locale)) return;
    if (this.locale$.value === locale) return;
    try { this.localStorage?.setItem(STORAGE_KEY, locale); } catch {}
    this.locale$.next(locale);
  }

  private readInitial(): AppLocale {
    // Фича ИИ-переводов выключена → всегда ru: переключатель скрыт, нельзя
    // залипнуть в EN из старого localStorage, Accept-Language остаётся ru.
    if (!this.environment.aiTranslation) return DEFAULT_LOCALE;
    try {
      const raw = this.localStorage?.getItem(STORAGE_KEY);
      if (raw && isSupportedLocale(raw)) return raw;
    } catch {}
    return DEFAULT_LOCALE;
  }
}
